import { useCallback, useEffect, useRef, useState } from "react";

import { toast } from "@/shared/hooks/use-toast";

import {
  acqService,
  type AcqCatalogItem,
  type AcqCatalogItemUpdate,
  type AcqCatalogView,
  type AcqPrefLevel,
  type AcqScene,
} from "../api/acqService";
import { errorToast, errorText } from "@/shared/lib/apiErrors";

/**
 * 发现目录的读 / 推荐 / 改(契约 §12.4)。
 *
 * ★ 三个写动作**全部就地替换那一项**,不重拉整页:目录有 15 张卡,重拉会让整页闪一下,
 *   而用户刚才只是拨了一个订阅开关。
 * ★ 首屏 `recommendedAt` 为空 ⇒ 自动推断**一次**(免费)。只一次、失败不重试 ——
 *   推不出来最常见的原因是 KB 为空(实测 33 个租户绝大多数 KB=0),那是**常态不是故障**,
 *   一直重试只会把一个永远不会成功的动作打一整天。
 */
export function useCatalog(initial?: AcqCatalogView | null) {
  const [view, setView] = useState<AcqCatalogView | null>(initial ?? null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!initial);
  /** ★ 落地页已经拉过一次就别再拉:`/ai-huoke` 要先读目录才知道该渲染哪一页。 */
  const seeded = useRef(!!initial);
  const [recommending, setRecommending] = useState(false);
  /** ★ 首屏自动推断的一次性闸。StrictMode 下 effect 会跑两遍,ref 才拦得住。 */
  const autoTried = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const v = await acqService.catalog();
      setView(v);
      setError(null);
      return v;
    } catch (e) {
      setError(errorText(e, { fallbackTitle: "目录没加载出来" }));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 重新推断(免费)。
   * ★ 0918 批次六(契约 §17.2):`productCode` 给了就**只重推这个产品** ——
   *   toast 也要说清是哪一个,否则用户按的是「只为这个产品再推一次」、
   *   看到的却是「已重新推断」,他没法判断别的产品的档有没有被动过。
   */
  const recommend = useCallback(
    async (opts: { silent?: boolean; productCode?: string; productName?: string } = {}) => {
      setRecommending(true);
      try {
        const v = await acqService.recommend(opts.productCode);
        setView(v);
        setError(null);
        if (!opts.silent) {
          toast({
            title: opts.productCode
              ? `已只为「${opts.productName ?? opts.productCode}」重推一次`
              : "已按知识库与行业包重新推断",
          });
        }
        return v;
      } catch (e) {
        setError(errorText(e, { fallbackTitle: "推断没跑起来" }));
        return null;
      } finally {
        setRecommending(false);
      }
    },
    []
  );

  const update = useCallback(async (type: string, patch: AcqCatalogItemUpdate) => {
    try {
      const row = await acqService.updateCatalogItem(type, patch);
      setView((prev) =>
        prev ? { ...prev, items: prev.items.map((i) => (i.type === row.type ? row : i)) } : prev
      );
      return row;
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "没保存上" }));
      return null;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const v = seeded.current ? initial : await load();
      seeded.current = false;
      if (!alive || !v || autoTried.current) return;
      if (!v.recommendedAt) {
        autoTried.current = true;
        await recommend({ silent: true });
      }
    })();
    return () => {
      alive = false;
    };
    // initial 只在首挂载读一次(它是落地页递进来的快照,不是响应式来源)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, recommend]);

  return { view, loading, error, recommending, reload: load, recommend, update };
}

/** 目录页的分区(契约 §12.8 四区 + §13.6 的「已隐藏」)。纯函数,好让分区规则能被单独钉住。 */
export interface CatalogPartition {
  p0: AcqCatalogItem[];
  p1: AcqCatalogItem[];
  /** 其余能跑的(P2 与未推断的),默认折在「更多」里。 */
  more: AcqCatalogItem[];
  comingSoon: AcqCatalogItem[];
  /**
   * 用户点过「不看这个」的(契约 §13.6)。折在「更多」**尾部**灰显,带「恢复」。
   * ★ 不是删掉:隐藏是"这阵子别烦我",不是"这个能力没有了"。删掉的话他想找回来时
   *   得先猜到这东西还存在 —— 而页面上再没有任何一处提过它。
   */
  hidden: AcqCatalogItem[];
  /**
   * 0918 批次七(契约 §18.1-2):**这个产品下用户说了「不看」的**(`productLevels[code] === "NA"`)。
   *
   * ★★ 与 `hidden` 有一条**刻意的不同**:`hidden` 是全局的"这阵子别烦我",折在「更多」尾部
   *   灰着还看得见;这一格是**真隐藏 —— 一张卡都不渲染**。上一版把它折进「更多」,
   *   用户点完「这个产品下不看」那张卡还在(只是换了个区),他会以为那一下没生效、再点一次。
   * ★ 但**不许无声地消失**:页签正下方那一行「在「X」下已隐藏 N 种 · 恢复」是它唯一的出口 ——
   *   没有那一行,他再也找不回自己藏掉的东西(而他记得的只是"我好像关过它")。
   * ★ 「全部」页签下**恒空**:这一格只在按某个产品看时才成立。
   */
  productHidden: AcqCatalogItem[];
}

/**
 * ★ 分区只看两件事:能不能跑(`status`)与推荐档(`level`)。
 * ★★ `COMING_SOON` 先于 level 判 —— 一个"主推但还没通"的种类必须落在「即将开通」,
 *   摆进主推区等于**展示假能力**(契约 §12.10):用户会去点一个不存在的按钮。
 * ★ P2(不建议)不藏起来,放在「更多」里并带上 why —— 说清"为什么不建议"比消失有用,
 *   否则用户只会觉得"你们没有这个功能"。
 */
/**
 * ★ 0917 批次二:**「不看这个」先于一切判**(契约 §13.6)——
 *   隐藏的那几种从主推 / 可试 / 即将开通里整个拿走,只在「更多」尾部灰着。
 *   不这么做的话,一个被主推档命中的种类点了「不看这个」还是会杵在最上面。
 * ★ 同档内按 `peerFeedback.shareBp` 降序(契约 §13.1 学习环 v1):
 *   同样是 P0,同行里真出过单的那一种该排前面。**没有 peerFeedback 的按 0 算**,
 *   于是它们排在有数据的后面 —— 而 `Array#sort` 稳定,没数据的几种彼此保持服务端顺序。
 */
export function partitionCatalog(items: readonly AcqCatalogItem[]): CatalogPartition {
  const p: CatalogPartition = {
    p0: [],
    p1: [],
    more: [],
    comingSoon: [],
    hidden: [],
    productHidden: [],
  };
  for (const i of items) {
    if (i.hidden) p.hidden.push(i);
    else if (i.status === "COMING_SOON") p.comingSoon.push(i);
    else if (i.level === "P0") p.p0.push(i);
    else if (i.level === "P1") p.p1.push(i);
    else p.more.push(i);
  }
  const rank: Record<AcqPrefLevel, number> = { P0: 0, P1: 1, P2: 2, NA: 3 };
  const share = (i: AcqCatalogItem) => i.peerFeedback?.shareBp ?? 0;
  const byLevelThenShare = (a: AcqCatalogItem, b: AcqCatalogItem) =>
    rank[a.level] - rank[b.level] || share(b) - share(a);
  p.p0.sort((a, b) => share(b) - share(a));
  p.p1.sort((a, b) => share(b) - share(a));
  p.comingSoon.sort(byLevelThenShare);
  p.more.sort(byLevelThenShare);
  return p;
}

// ═══════════════════════════════════════════════════════════════════════════
// 0917 批次五:场景 · 按产品重算分区(契约 §16.1 / §16.7)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 这一条线**在这个产品下**被哪几个场景引用了。
 *
 * ★ 两头都要认:场景那边写的是 `sources: [type…]`,卡这边写的是 `sceneIds: [id…]` ——
 *   任一头缺席(后端分批上线)都不该让另一头失效,所以**两边都命中才算**是错的,
 *   这里取的是**并集**:`scene.sources` 点名了这条线,或者这条线自己认领了这个场景 id。
 * ★ `productCode` 给了值 ⇒ 只算挂在这个产品下的场景(`scene.productCode === code`);
 *   给空串 ⇒ 全算(「全部」页签下卡上照显它的全部场景)。
 * ★ 场景的 `productCode` 为 null(推不出挂哪个产品)时**不算进任何一个产品**:
 *   把它算进去等于替 AI 认领一个它没做的绑定,而用户看到的是"这个产品有场景"。
 */
export function scenesOf(
  item: Pick<AcqCatalogItem, "type" | "sceneIds">,
  scenes: readonly AcqScene[],
  productCode = ""
): AcqScene[] {
  const ids = new Set(item.sceneIds ?? []);
  return scenes.filter((s) => {
    if (productCode && (s.productCode ?? "") !== productCode) return false;
    return ids.has(s.id) || (s.sources ?? []).includes(item.type);
  });
}

/**
 * 定档时算数的那几个场景(契约 §17.2:`weak` 的**不参与定档**,covers 不算它)。
 *
 * ★★ 为什么单独一个函数:卡面要显示 `weak` 的(灰着、标「材料不足,AI 猜的」),
 *   分区**不能**算它。同一份场景表两种用法,判据只放一处 —— 写在两个地方必漂,
 *   而漂的形态是"一条猜出来的场景把一条线顶进主推区",用户照着它花了钱。
 */
export function strongScenesOf(
  item: Pick<AcqCatalogItem, "type" | "sceneIds">,
  scenes: readonly AcqScene[],
  productCode = ""
): AcqScene[] {
  return scenesOf(item, scenes, productCode).filter((s) => s.weak !== true);
}

/**
 * 这个产品下用户手调过的档(契约 §17.2 末句:**`productLevels` 优先于场景重算**)。
 * 没调过返回 `null` —— `null` 与 `"NA"` 是两件事:前者是"用户没说过话",后者是"他说不看"。
 */
export function productLevelOf(
  item: Pick<AcqCatalogItem, "productLevels">,
  productCode: string
): AcqPrefLevel | null {
  if (!productCode) return null;
  return item.productLevels?.[productCode] ?? null;
}

/**
 * 选中这个产品时,这条线**实际会拿去搜**的词(契约 §16.2;0921 改口径)。
 *
 * ★★ 卡上写的必须与真去搜的是同一批 —— 用户是照着卡上那几个词决定花不花这笔钱的。
 *   所以这里不自己算,读后端发下来的 `searchKeywordsByProduct[code]`
 *   (= 通用词在前 + 这个产品自己的词,与 `POST /discover/run` 取词同一个函数)。
 * ★★ 0921 之前读的是 `keywordsByProduct[code]` 原样。那张表只有 AI 给的词;用户在卡上点
 *   「编辑」加的词只进扁平表 —— 于是产品页签下「加了词卡上看不见、搜索也没用上」。
 * ★ AI 的行**没给这个产品配词**时仍返回空(灰一行、「发现一次」禁用,老规矩不变):
 *   那时后端会退回用整条线的词,那不是「按这个产品找」。
 *   只有**用户自己改过词**的卡,通用词才顶上来 —— 那几个词是他亲手写的,
 *   而且这种卡「重新推断」不会再动它,再劝他去重新推断是条死路。
 * ★ 老后端不发新字段 ⇒ 退回 `keywordsByProduct[code]`(旧行为),不猜。
 */
export function productKeywords(
  item: Pick<
    AcqCatalogItem,
    | "keywords"
    | "keywordsByProduct"
    | "keywordsSource"
    | "genericKeywords"
    | "searchKeywordsByProduct"
  >,
  productCode: string
): string[] {
  if (!productCode) return item.keywords ?? [];
  const own = item.keywordsByProduct?.[productCode] ?? [];
  const userEdited = item.keywordsSource === "user";
  if (own.length === 0 && !userEdited) return [];
  const effective = item.searchKeywordsByProduct?.[productCode];
  if (effective) return effective;
  return userEdited && own.length === 0 ? (item.genericKeywords ?? []) : own;
}

/**
 * 选中一个产品之后的分区(契约 §16.7)。
 *
 * ★★ 这是老板那句「我们这个仓应该都是根据产品来驱动的」的落点:选中产品后,
 *   **全局档位整个让位** —— 一条对知产全局是 P0 的线,对「高企认定」这个产品可能
 *   一个场景都没引用、一个词都没配,那它就不该还杵在主推区。
 * ★ 三档的判据逐字照契约:
 *   ① 被**这个产品的场景**引用 → 主推;
 *   ② 有这个产品的词组但没被场景引用 → 可试;
 *   ③ 其余 → 更多。
 * ★ `hidden` 与 `COMING_SOON` 的优先级**不变**(先于三档判):
 *   隐藏是"这阵子别烦我",而把一个还没接通的种类摆进主推区是**展示假能力**(契约 §12.10)
 *   —— 这两条与按不按产品看没关系。
 * ★ 同档内仍按 `peerFeedback.shareBp` 降序(与全局分区同一把尺)。
 */
export function partitionCatalogByProduct(
  items: readonly AcqCatalogItem[],
  scenes: readonly AcqScene[],
  productCode: string
): CatalogPartition {
  const p: CatalogPartition = {
    p0: [],
    p1: [],
    more: [],
    comingSoon: [],
    hidden: [],
    productHidden: [],
  };
  for (const i of items) {
    // ★★ 0918 批次七(契约 §18.1-2):「这个产品下不看」**先于一切判** —— 包括
    //   `COMING_SOON`。它落在别的区里都等于"点了没反应":用户刚说过不看,那张卡还在页面上。
    //   (全局 `hidden` 仍排在它前面:那一档在「更多」尾部有自己的恢复入口,
    //   两个入口抢同一张卡只会让他在两处各点一次。)
    if (productLevelOf(i, productCode) === "NA" && !i.hidden) p.productHidden.push(i);
    else if (i.hidden) p.hidden.push(i);
    else if (i.status === "COMING_SOON") p.comingSoon.push(i);
    else {
      // ★★ 0918 批次六(契约 §17.2):用户在这个产品下说过的话**先于**场景重算。
      //   `P2` 落「更多」(带着 why 说清为什么不建议);`NA` 在上面就被拿走了。
      const mine = productLevelOf(i, productCode);
      if (mine === "P0") p.p0.push(i);
      else if (mine === "P1") p.p1.push(i);
      else if (mine) p.more.push(i);
      // ★ 定档只认**不 weak** 的场景(猜出来的场景不许把一条线顶进主推区)。
      else if (strongScenesOf(i, scenes, productCode).length > 0) p.p0.push(i);
      else if (productKeywords(i, productCode).length > 0) p.p1.push(i);
      else p.more.push(i);
    }
  }
  const share = (i: AcqCatalogItem) => i.peerFeedback?.shareBp ?? 0;
  const byShare = (a: AcqCatalogItem, b: AcqCatalogItem) => share(b) - share(a);
  p.p0.sort(byShare);
  p.p1.sort(byShare);
  p.comingSoon.sort(byShare);
  p.more.sort(byShare);
  return p;
}
